/**
 * Silo Anywhere — on-platform appearance (P7).
 *
 * Mounts the REAL loader (`/widget.js`) on this silo's own creator pages, so
 * the owner and their collaborators meet the assistant exactly as a visitor
 * will on their website: same iframe, same chat UI, same owner-sponsored
 * access model. There is no second renderer and no React chat.
 *
 * Scope is deliberately narrow, per Randy: a silo may have an assistant, the
 * platform does not. This renders ONLY inside /dashboard/silo/:siloId. Never
 * on the Public Q&A page, the Fan Portal, member pages or the dashboard.
 *
 * The session comes from the server first: a 409 means the assistant simply is
 * not switched on for these pages, and the right behaviour then is to draw
 * nothing and say nothing.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export default function SiloAnywhereSelfHost({ siloId }) {
  const [nonce, setNonce] = useState(0);

  // The settings card and the identity-pill switch both fire this, so flipping
  // either one takes effect on the page you are standing on.
  useEffect(() => {
    const bump = () => setNonce((n) => n + 1);
    window.addEventListener("mysilo:silo-anywhere-changed", bump);
    return () => window.removeEventListener("mysilo:silo-anywhere-changed", bump);
  }, []);

  useEffect(() => {
    if (!siloId) return undefined;
    let alive = true;
    let el = null;
    let token = null;
    (async () => {
      try {
        const r = await api.post(`/silos/${siloId}/widget/preview-session`);
        if (!alive || !r.data?.preview_token) return;
        token = r.data.widget_token;
        el = document.createElement("script");
        el.async = true;
        el.src = r.data.loader_url;
        el.setAttribute("data-widget-token", token);
        el.setAttribute("data-preview-token", r.data.preview_token);
        el.setAttribute("data-testid", "silo-anywhere-selfhost-loader");
        document.body.appendChild(el);
      } catch { /* not switched on for these pages, or no access */ }
    })();
    return () => {
      alive = false;
      // The loader is a singleton that outlives this route, so the launcher has
      // to be taken down explicitly or it would follow the user off the silo.
      try { if (token) window.__MY_SILO_ANYWHERE__?.unmount(token); } catch { /* */ }
      if (el?.parentNode) el.parentNode.removeChild(el);
    };
  }, [siloId, nonce]);

  return null;
}
